import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';

import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getSwitchButtonColumnOptions } from 'src/utils/grid/GridHandlerContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultTQC5020_R01DetailDto, TQC5020_R01DetailDto } from './Dto_TQC5020_R01';
import { useTQC5020_R01Store } from './Store_TQC5020_R01';

const COLUMN_STATE_KEY = 'TQC5020_R01Spec-grid-column-state';

const headerNames: Record<string, string> = {
	rowIndex: '',
	inspTypeNm: '검사구분',
	inspItemUsage: '사용여부',
	inspItemCd: '항목코드',
	inspItemNm: '항목명',
	specValue1: '값1',
	specRangeNm: '범위',
	specValue2: '값2',
	inspUnit: '단위',
	valueTypeNm: '값유형',
	inspMethodNm: '검사방법',
};

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC5020_R01DetailDto> | null>;
}

export const Table_TQC5020_R01Spec = ({ gridRef }: Props) => {
	const {
		TQC5020_R01DetailList: specList,
		updateTQC5020_R01DetailListList: updateSpecList,
		originalTQC5020_R01DetailListList: originalSpecList,
		TQC5020_R01DetailListColumnState: specColumnState,
		setTQC5020_R01DetailListColumnState: setSpecColumnState,
		specScrollTop, setSpecScrollTop,
	} = useTQC5020_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: specList,
		defaultGridData: defaultTQC5020_R01DetailDto,
		selectedRow: [],
		originalGridData: originalSpecList,
		updateRow: (index, data) => updateSpecList(index, data),
	});

	const handleSpecUsageSwitch = useCallback((
		valueIndex: number,
		params: { data: TQC5020_R01DetailDto },
		newValue: 'Y' | 'N',
	) => {
		handleSwitchButtonHandler(valueIndex, params, newValue, headerNames);
	}, [handleSwitchButtonHandler]);

	const baseColumnDefs: ColDef<TQC5020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '검사구분', colId: 'inspTypeNm', field: 'inspTypeNm', width: 100,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '사용여부', colId: 'inspItemUsage', field: 'inspItemUsage', width: 75,
			...getSwitchButtonColumnOptions('inspItemUsage', handleSpecUsageSwitch, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: '항목코드', colId: 'inspItemCd', field: 'inspItemCd', width: 100,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '항목명', colId: 'inspItemNm', field: 'inspItemNm', width: 200,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '값1', colId: 'specValue1', field: 'specValue1', width: 80,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '범위', colId: 'specRangeNm', field: 'specRangeNm', width: 70,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '값2', colId: 'specValue2', field: 'specValue2', width: 80,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '단위', colId: 'inspUnit', field: 'inspUnit', width: 70,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '값유형', colId: 'valueTypeNm', field: 'valueTypeNm', width: 90,
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '검사방법', colId: 'inspMethodNm', field: 'inspMethodNm', width: 90,
			editable: false,
			headerClass: aggridHeaderClass,
		},
	], [handleSpecUsageSwitch]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC5020_R01DetailDto>[] = useMemo(() => {
		if (specColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<TQC5020_R01DetailDto>({
			baseColumnDefs,
			columnState: specColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [specColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		inspItemCd: `${specList.length.toLocaleString()}건`,
	}]), [specList]);

	return (
		<GridContextWrapper<TQC5020_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={specList}
			initSelected={() => {}}
			defaultRowData={defaultTQC5020_R01DetailDto}
			baseColumnDefs={specColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setSpecColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
			enableExcelPaste={false}
		>
			<Grid_Excel<TQC5020_R01DetailDto>
				ref={gridRef}
				rowData={specList}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setSpecColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={specScrollTop}
				setScrollTop={setSpecScrollTop}
			/>
		</GridContextWrapper>
	);
};
